import { devLog } from '@/utils/dev';
import { generateRandomString, toBoolean } from '@/utils/string';

type TCredentialUser = {
  id: string;
  name: string;
  displayName: string;
};

type TCredentialOptions = {
  challenge?: string;
  credentialIds?: string[];
  timeout?: number;
};

export type TCreateCredentialReturn = {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
  };
};

export type TGetCredentialReturn = {
  id: string;
  rawId: string;
  type: string;
  response: {
    signature: string;
    userHandle: string;
    clientDataJSON: string;
    authenticatorData: string;
  };
};

export const isAuthenticatorSupported = async (): Promise<boolean> => {
  try {
    const isEnabled = toBoolean(
      process.env.NEXT_PUBLIC_PASSCODE_SUPPORT_AUTHENTICATOR!
    );

    if (!isEnabled) {
      throw new Error('Disabled by config');
    }

    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (e) {
    return Promise.resolve(false);
  }
};

export const createCredential = async (
  user: TCredentialUser,
  options: TCredentialOptions = {}
): Promise<TCreateCredentialReturn> => {
  try {
    const credential: Credential | any = await navigator.credentials.create({
      publicKey: {
        challenge: Buffer.fromString(
          options?.challenge || generateRandomString(16)
        ),
        rp: { id: document.domain, name: 'Mofid App' },
        user: {
          id: Buffer.fromString(user.id),
          name: user.name,
          displayName: user.displayName,
        },
        excludeCredentials: getCredentialIdObjects(
          options?.credentialIds || []
        ),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'preferred',
        },
        timeout: options?.timeout ?? 2 * 60 * 1000, // milliseconds
      },
    });

    devLog('credential@get@data', credential);
    // return credential;

    return {
      id: credential?.id,
      rawId: Buffer.toString(credential?.rawId),
      type: credential?.type,
      response: {
        clientDataJSON: Buffer.toString(credential?.response?.clientDataJSON),
        attestationObject: Buffer.toString(
          credential?.response?.attestationObject
        ),
      },
    };
  } catch (error) {
    devLog('credential@create@error', error);
    return Promise.reject(error);
  }
};

export const getCredential = async (
  options: TCredentialOptions = {}
): Promise<TGetCredentialReturn> => {
  try {
    const credentialIds = options?.credentialIds || [];

    const assertion: Credential | any = await navigator.credentials.get({
      publicKey: {
        challenge: Buffer.fromString(
          options?.challenge || generateRandomString(16)
        ),
        allowCredentials: getCredentialIdObjects(credentialIds),
        userVerification: 'preferred',
        timeout: options?.timeout ?? 7 * 1000, // milliseconds
      },
    });

    devLog('credential@get@data', assertion);
    //return assertion;

    return {
      id: assertion?.id,
      rawId: Buffer.toString(assertion?.rawId),
      type: assertion.type,
      response: {
        signature: Buffer.toString(assertion?.response?.signature),
        userHandle: Buffer.toString(assertion?.response?.userHandle),
        clientDataJSON: Buffer.toString(assertion?.response?.clientDataJSON),
        authenticatorData: Buffer.toString(
          assertion?.response?.authenticatorData
        ),
      },
    };
  } catch (error) {
    devLog('credential@get@error', error);
    return Promise.reject(error);
  }
};

//Helpers -----------------------

const getCredentialIdObjects = (
  credentialIds: string[]
): PublicKeyCredentialDescriptor[] => {
  return credentialIds.filter(Boolean).map((credentialId: string) => ({
    id: Buffer.fromString(credentialId),
    type: 'public-key',
    transports: ['internal'],
  }));
};

const Buffer = {
  fromString: function (baseurl64String: string): ArrayBuffer {
    const padding = '=='.slice(0, (4 - (baseurl64String.length % 4)) % 4);
    const str = atob(
      baseurl64String.replace(/-/g, '+').replace(/_/g, '/') + padding
    );
    const buffer = new ArrayBuffer(str.length);
    const byteView = new Uint8Array(buffer);
    for (let i = 0; i < str.length; i++) {
      byteView[i] = str.charCodeAt(i);
    }
    return buffer;
  },
  toString: function (buffer: ArrayBuffer): string {
    let str = '';
    const byteView: any = new Uint8Array(buffer);
    for (const charCode of byteView) {
      str += String.fromCharCode(charCode);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  },
};
